import type {
  FiatStepResponse,
  SubmitTransition,
  Surface,
  SurfaceResult,
} from '@funkit/connect-core'

import type { StripeAuthorizeResult, StripeOnrampError } from './stripeOnramp'

export interface StripeSessionAuthStart {
  transition: SubmitTransition
  verifiedEmail: string
}

/**
 * Reads the frozen Stripe auth-start seam from a generic `SESSION_AUTH` step.
 * The backend owns the email and transition; the client only runs its local
 * Link-account prerequisite before firing them.
 */
export function stripeSessionAuthStart(
  step: FiatStepResponse | undefined,
  ctas: readonly SubmitTransition[],
): StripeSessionAuthStart | undefined {
  if (
    step?.provider !== 'STRIPE' ||
    step.state.kind !== 'SESSION_AUTH' ||
    step.state.surface !== undefined ||
    // An EMAIL_OTP or SMS_OTP round is collected on our own screen. Without
    // this, `computePage` puts the buyer on the OTP screen (computePage.ts:148
    // routes only PROVIDER_RENDERED away from it) while Link opens behind it.
    step.state.channel !== 'PROVIDER_RENDERED'
  ) {
    return undefined
  }

  for (const transition of ctas) {
    // `resend` re-sends the code carrying the same `verifiedEmail`, so it
    // satisfies every other test here and can be picked as the auth start.
    // `entryCta.ts:155` excludes it by id when it picks the session opening.
    if (transition.id === RESEND_ID) {
      continue
    }
    const verifiedEmail = transition.params?.verifiedEmail
    if (typeof verifiedEmail === 'string' && looksLikeEmail(verifiedEmail)) {
      return { transition, verifiedEmail }
    }
  }
  return undefined
}

export type StripeAuthSurface =
  | { action: 'AUTHORIZE'; linkAuthIntentId: string }
  | {
      action: 'AUTHENTICATE_WITH_TOKEN'
      linkAuthTokenClientSecret: string
    }

/** Narrows the two Stripe native authentication surfaces at the adapter boundary. */
export function stripeAuthSurface(
  surface: Surface,
): StripeAuthSurface | undefined {
  if (surface.kind !== 'AUTH_COMPONENT' || surface.provider !== 'STRIPE') {
    return undefined
  }
  // The surface crosses the wire, so `params` is only as trustworthy as the
  // step that carried it. A missing object here threw a TypeError instead of
  // falling through to the unsupported-surface screen.
  const wire: unknown = surface.params
  if (wire === null || typeof wire !== 'object') {
    return undefined
  }
  const params = wire as Record<string, unknown>

  if (params.action === 'AUTHENTICATE_WITH_TOKEN') {
    const clientSecret = params.linkAuthTokenClientSecret
    return typeof clientSecret === 'string' && clientSecret.length > 0
      ? {
          action: 'AUTHENTICATE_WITH_TOKEN',
          linkAuthTokenClientSecret: clientSecret,
        }
      : undefined
  }
  const linkAuthIntentId = params.linkAuthIntentId
  return (params.action === undefined || params.action === 'AUTHORIZE') &&
    typeof linkAuthIntentId === 'string' &&
    linkAuthIntentId.length > 0
    ? { action: 'AUTHORIZE', linkAuthIntentId }
    : undefined
}

/**
 * Flat, like `stripeAuthorizationReport` beside it and for the same reason: this
 * surface answers a SUBMIT, and `injectExpects` fills nothing for a transition
 * that is not a `CLIENT_SURFACE`. Nested under `result` the outcome was dropped
 * during body assembly, so the buyer's consent never left the device.
 */
export const stripeSeamlessAuthReport = (
  authenticated: boolean,
): SurfaceResult => ({
  outcome: authenticated ? 'Authenticated' : 'Failed',
})

/**
 * Maps Stripe's SDK union onto the frozen wire outcomes, one key per declared
 * input. Flat rather than nested under `result`: the transition carrying this
 * is a SUBMIT, so body assembly takes each value by the input name the
 * envelope declares, and `customerId` stays conditional by being absent on
 * anything but consent rather than by being undefined inside a wrapper.
 */
export function stripeAuthorizationReport(
  result: StripeAuthorizeResult,
): SurfaceResult | undefined {
  if (result.status === 'Consented') {
    return result.customerId === undefined
      ? undefined
      : { outcome: result.status, customerId: result.customerId }
  }
  if (result.status === 'Denied') {
    return { outcome: result.status }
  }
  if (isStripeCancellation(result.error)) {
    return { outcome: 'Cancelled' }
  }
  return undefined
}

/**
 * Takes the error optionally. The SDK's result is declared as a union whose
 * failure member always carries one, but an undeclared shape does reach here at
 * runtime, and reading `.code` off it threw rather than reporting no outcome.
 */
export function isStripeCancellation(error?: StripeOnrampError): boolean {
  return error?.code === 'Canceled'
}

/** Spelled as `entryCta.ts:160` spells it; connect-core does not export it. */
const RESEND_ID = 'resend'

const looksLikeEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
